import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

interface SepaTaskClient {
    id: string;
    salonName?: string;
    ownerName?: string;
    customerNumber?: string;
    billingActivatedAt?: string;
}

const StealthSepaRadar: React.FC = () => {
    const [tasks, setTasks] = useState<SepaTaskClient[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [processingId, setProcessingId] = useState<string | null>(null);

    useEffect(() => {
        const q = query(
            collection(db, 'kalkulation_mandanten'),
            where('sepaCancelPending', '==', true)
        );

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const pendingList: SepaTaskClient[] = [];
                snapshot.forEach((docSnap) => {
                    const d: any = docSnap.data();
                    const salon = d.firmenname || d.clientData?.salonname || d.clientData?.firmenname || 'Unbenannter Salon';

                    // Formatierung strikt: Vorname Nachname
                    const firstName = d.vorname || d.clientData?.vorname || '';
                    const lastName = d.nachname || d.clientData?.nachname || '';
                    const fullName = `${firstName} ${lastName}`.trim();
                    const owner = fullName || d.clientData?.inhaber || d.ownerName || undefined;

                    pendingList.push({
                        id: docSnap.id,
                        salonName: salon,
                        ownerName: owner,
                        customerNumber: d.customerNumber || d.mandantNr || d.providedOldId,
                        billingActivatedAt: d.billingActivatedAt,
                    });
                });

                // Sortierung alphabetisch nach Inhaber (Vorname Nachname) bzw. Salonname
                pendingList.sort((a, b) => {
                    const nameA = a.ownerName || a.salonName || '';
                    const nameB = b.ownerName || b.salonName || '';
                    return nameA.localeCompare(nameB, 'de', { sensitivity: 'base' });
                });

                setTasks(pendingList);
            },
            (error) => {
                console.warn('StealthSepaRadar snapshot warning:', error);
            }
        );

        return () => unsubscribe();
    }, []);

    // NULL-NOISE-PRINZIP: Komplett unsichtbar, wenn kein Task offen ist
    if (tasks.length === 0) return null;

    const handleResolveTask = async (mandantId: string) => {
        setProcessingId(mandantId);
        try {
            const mandantRef = doc(db, 'kalkulation_mandanten', mandantId);
            await updateDoc(mandantRef, {
                sepaCancelPending: false,
                sepaCanceledAt: new Date().toISOString(),
            });
        } catch (err) {
            console.error('Fehler beim Quittieren des SEPA-Tasks:', err);
        } finally {
            setProcessingId(null);
        }
    };

    return (
        <>
            <button
                onClick={() => setIsModalOpen(true)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-xl bg-amber-50 border border-amber-500 text-amber-900 hover:bg-amber-100 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-400 dark:hover:bg-amber-500/20 transition-all shadow-[0_0_12px_rgba(245,158,11,0.15)] animate-pulse cursor-pointer"
                title="Offene SEPA-Kündigungen bei der Hausbank"
            >
                <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500 dark:bg-amber-400" />
                    <span className="text-xs font-bold tracking-wide uppercase">SEPA-Radar</span>
                </div>
                <span className="text-xs font-mono font-bold px-1.5 py-0.5 rounded bg-amber-200 text-amber-950 dark:bg-amber-500/20 dark:text-amber-300">
                    {tasks.length}
                </span>
            </button>

            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="w-full max-w-2xl carbon-panel carbon-weave-bg border border-amber-500/30 rounded-2xl p-6 shadow-2xl space-y-5">
                        <div className="flex items-center justify-between border-b border-white/10 pb-4">
                            <div className="flex items-center gap-3">
                                <span className="w-3 h-3 rounded-full bg-amber-400 animate-pulse" />
                                <h3 className="text-lg font-bold text-stone-100">
                                    Stealth-SEPA-Radar: Offene Hausbank-Kündigungen
                                </h3>
                            </div>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                className="text-stone-400 hover:text-white text-sm font-semibold cursor-pointer"
                            >
                                ✕ Schließen
                            </button>
                        </div>

                        <p className="text-xs text-stone-400 leading-relaxed">
                            Folgende Bestandskunden wurden auf Stripe umgestellt. Bitte das alte SEPA-Lastschriftmandat bei der Hausbank deaktivieren und hier quittieren:
                        </p>

                        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                            {tasks.map((task) => (
                                <div
                                    key={task.id}
                                    className="flex items-center justify-between p-4 rounded-xl bg-black/40 border border-white/5 hover:border-white/10 transition-colors"
                                >
                                    <div className="space-y-1">
                                        <div className="text-sm font-bold text-stone-200">{task.salonName}</div>
                                        <div className="text-xs text-stone-400 flex items-center gap-3">
                                            <span>Mandant-Nummer: <strong className="text-amber-400/90">{task.customerNumber || '–'}</strong></span>
                                            {task.ownerName && <span>Inh.: {task.ownerName}</span>}
                                        </div>
                                        {task.billingActivatedAt && (
                                            <div className="text-[10px] text-stone-500">
                                                Aktiviert am: {new Date(task.billingActivatedAt).toLocaleDateString('de-DE')}
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        disabled={processingId === task.id}
                                        onClick={() => handleResolveTask(task.id)}
                                        className="px-3 py-2 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-500 active:bg-amber-700 disabled:opacity-50 rounded-lg transition shadow-md whitespace-nowrap cursor-pointer"
                                    >
                                        {processingId === task.id ? 'Wird quittiert...' : 'SEPA bei Bank erledigt ✓'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default StealthSepaRadar;
